import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Add from '@material-ui/icons/Add';
import { Tooltip } from '@ublend-npm/aulaui-next';
import MaterialListItem from '../MaterialList/MaterialListItem.container';
import MaterialMenu from '../MaterialList/MaterialMenu.container';
import RenameFolderDialog from '../MaterialList/RenameFolderDialog.container';
import DeleteFolderDialog from '../MaterialList/DeleteFolderDialog.container';
import EmptyMaterialList from '../MaterialList/EmptyMaterialList';
import {
  AddButton,
  MaterialListTitle,
  MaterialListWrapper,
  MaterialListScrollWrapper,
  MaterialListItemsWrapper,
} from './MaterialListEducatorOnly.styled';
import ScrollBar from '../../../common/ScrollBar';
import RestoreMaterialDrawer from '../MaterialList/RestoreMaterialDrawer.container';
import {
  MATERIAL_PERMISSIONS,
  evaluateMaterialAction,
} from '@core/materials/permissions';

const MaterialListEducatorOnly = ({
  classRoomId,
  onAddClick,
  onPermissionDenied,
  materialEducatorOnlyIds,
  isFetchingSections,
  canCreate = false,
  permissions = [],
  isRbacEnabled = false,
  canView = true,
  isStudent = false,
}) => {
  const [restoring, setRestoring] = useState(false);

  if (!canView) {
    return null;
  }

  // If RBAC is disabled, always use canCreate as fallback (legacy behavior)
  // If RBAC is enabled, only use fallback if no permissions are resolved
  const hasResolvedPermissions =
    Array.isArray(permissions) && permissions.length > 0;
  const fallbackAllowed = !isRbacEnabled
    ? canCreate
    : !hasResolvedPermissions && canCreate;

  // If RBAC is disabled, pass empty permissions to force fallback usage
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  const createStatus = evaluateMaterialAction('CREATE_PAGE', {
    permissions: effectivePermissions,
    fallback: fallbackAllowed,
  });

  const handleAddClick = () => {
    if (!createStatus.allowed) {
      onPermissionDenied(MATERIAL_PERMISSIONS.CREATE, classRoomId);
      return;
    }
    onAddClick();
  };

  // For students: only show "+" button if they have permission (hide if no permission)
  // For educators: show button (disabled with tooltip if no permission)
  const shouldShowAddButton = isStudent
    ? isRbacEnabled && createStatus.allowed
    : true;

  return (
    <MaterialListWrapper>
      <ScrollBar autoHide>
        <MaterialListScrollWrapper>
          <MaterialListTitle
            component="div"
            title="Materials"
            testID="menu-materials"
          >
            <>
              <MaterialMenu
                educatorOnly
                onRestorePage={() => setRestoring(true)}
                isStudent={isStudent}
              />
              <RestoreMaterialDrawer
                classRoomId={classRoomId}
                educatorArea
                open={restoring}
                onClose={() => setRestoring(false)}
              />
              {shouldShowAddButton && (
                <Tooltip
                  title={
                    createStatus.allowed ? 'New page' : createStatus.message
                  }
                  space={4}
                  position="right"
                >
                  <div>
                    <AddButton
                      type="secondary"
                      size="xSmall"
                      icon={Add}
                      label="Create page"
                      onClick={handleAddClick}
                    />
                  </div>
                </Tooltip>
              )}
            </>
          </MaterialListTitle>
          <MaterialListItemsWrapper>
            {materialEducatorOnlyIds.length || isFetchingSections ? (
              materialEducatorOnlyIds.map((id, index) => (
                <MaterialListItem
                  key={id}
                  materialId={id}
                  educatorOnly
                  data-testid={`educator-material-${index + 1}`}
                />
              ))
            ) : (
              <EmptyMaterialList isInstructor={createStatus.allowed} />
            )}
          </MaterialListItemsWrapper>

          <RenameFolderDialog />
          <DeleteFolderDialog educatorOnly />
        </MaterialListScrollWrapper>
      </ScrollBar>
    </MaterialListWrapper>
  );
};

MaterialListEducatorOnly.propTypes = {
  classRoomId: PropTypes.string.isRequired,
  onAddClick: PropTypes.func.isRequired,
  onPermissionDenied: PropTypes.func,
  materialEducatorOnlyIds: PropTypes.arrayOf(PropTypes.string),
  isFetchingSections: PropTypes.bool.isRequired,
  canCreate: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  canView: PropTypes.bool,
  isStudent: PropTypes.bool,
};

MaterialListEducatorOnly.defaultProps = {
  materialEducatorOnlyIds: [],
  onPermissionDenied: () => {},
  canCreate: false,
  permissions: [],
  isRbacEnabled: false,
  canView: true,
  isStudent: false,
};

export default React.memo(MaterialListEducatorOnly);
